import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ClipboardCheck, Plus, Pencil, Trash2, Copy, Archive, ChevronDown, ChevronUp,
  FileText, Users, Calendar, Clock, Search, Filter, Loader2, CheckCircle2,
  GripVertical, Eye, Send, Settings, RotateCcw, X, AlertCircle, User, MessageSquare, Tag, Shield,
  Upload, Image as ImageIcon, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'file', label: 'File Upload' }
];

const TeamActivityReports = ({ currentUser, getAuthToken }) => {
  const [activeSubTab, setActiveSubTab] = useState('submit');
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  
  // Report Reasons State (NEW)
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [reasonTemplates, setReasonTemplates] = useState([]);
  const [loadingReasonTemplates, setLoadingReasonTemplates] = useState(false);
  
  // Manage Reasons State (Admin)
  const [allReasons, setAllReasons] = useState([]);
  const [isReasonDialogOpen, setIsReasonDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [newReasonName, setNewReasonName] = useState('');
  const [newReasonDescription, setNewReasonDescription] = useState('');
  const [newReasonTemplateIds, setNewReasonTemplateIds] = useState([]);
  const [newReasonOrder, setNewReasonOrder] = useState(0);
  const [newReasonActive, setNewReasonActive] = useState(true);
  const [savingReason, setSavingReason] = useState(false);
  
  // Report Access State (Admin - for QC/QA users)
  const [reportAccessUsers, setReportAccessUsers] = useState([]);
  const [loadingReportAccess, setLoadingReportAccess] = useState(false);
  
  // Submit Report State
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Outcomes Filters
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    department: '',
    employee: '',
    templateId: '',
    status: '',
    search: ''
  });
  
  // Form Builder State
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateDepartments, setNewTemplateDepartments] = useState([]);
  const [newTemplateJobIds, setNewTemplateJobIds] = useState([]);
  
  // Question Editor State
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  
  // Outcome Edit State (Admin Only)
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [isEditSubmissionDialogOpen, setIsEditSubmissionDialogOpen] = useState(false);
  const [editSubmissionResponses, setEditSubmissionResponses] = useState({});
  const [editSubmissionStatus, setEditSubmissionStatus] = useState('');
  const [savingSubmission, setSavingSubmission] = useState(false);
  
  // Bulk Selection State for Outcomes
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [deletingSubmissions, setDeletingSubmissions] = useState(false);
  
  // Date-wise grouping state for Outcomes
  const [expandedDates, setExpandedDates] = useState({}); // { 'YYYY-MM-DD': true/false }
  
  // QC/QA Review State
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewDefects, setReviewDefects] = useState([]);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  
  // QA Feedback to QC State
  const [qaFeedbackDialogOpen, setQaFeedbackDialogOpen] = useState(false);
  const [qaFeedbackText, setQaFeedbackText] = useState('');
  const [qaFeedbackSubmission, setQaFeedbackSubmission] = useState(null);
  const [savingQaFeedback, setSavingQaFeedback] = useState(false);
  
  // Comment/Update State
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentSubmission, setCommentSubmission] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isUpdateComment, setIsUpdateComment] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  
  // File upload state
  const [uploadingFiles, setUploadingFiles] = useState({}); // { questionId: true/false }
  
  const isAdmin = currentUser?.is_admin;
  const isTeamLead = currentUser?.is_team_lead;
  const isQC = currentUser?.role === 'qc' || currentUser?.is_qc;
  const isQA = currentUser?.role === 'qa' || currentUser?.is_qa;
  const canReview = isAdmin || isQC || isQA; // Can review submissions
  const canGiveQAFeedback = isAdmin || isQA; // Can give feedback to QC

  // Helper to check if a string is an image URL or image filename
  const isImageResponse = (value) => {
    if (!value || typeof value !== 'string') return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    const lowerValue = value.toLowerCase();
    return imageExtensions.some(ext => lowerValue.endsWith(ext)) || 
           lowerValue.includes('/api/uploads/') || 
           lowerValue.includes('/uploads/');
  };

  // Helper to check if response has a valid URL (not just filename)
  const hasValidImageUrl = (value) => {
    if (!value || typeof value !== 'string') return false;
    return value.startsWith('/api/') || 
           value.startsWith('/uploads/') || 
           value.startsWith('http') || 
           value.startsWith('data:');
  };

  // Helper to get full image URL
  const getImageUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('http') || value.startsWith('data:')) return value;
    if (value.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${value}`;
    if (value.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${value}`;
    return value;
  };

  // Upload file to server
  const handleFileUpload = async (questionId, file) => {
    if (!file) return;
    
    setUploadingFiles(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store the returned URL in responses
      handleResponseChange(questionId, res.data.url);
      toast.success('File uploaded successfully');
    } catch (e) {
      console.error('File upload error:', e);
      toast.error(e.response?.data?.detail || 'Failed to upload file');
      // Fallback: store filename if upload fails
      handleResponseChange(questionId, file.name);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [questionId]: false }));
    }
  };

  // Fetch Report Reasons
  const fetchReasons = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/reports/reasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReasons(res.data || []);
    } catch (e) {
      console.error('Error fetching reasons:', e);
    }
  }, [getAuthToken]);

  // Fetch All Reasons (Admin)
  const fetchAllReasons = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/reports/reasons/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllReasons(res.data || []);
    } catch (e) {
      console.error('Error fetching all reasons:', e);
    }
  }, [getAuthToken, isAdmin]);

  // Fetch QC/QA users for report access management
  const fetchReportAccessUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingReportAccess(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/users/report-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportAccessUsers(res.data || []);
    } catch (e) {
      console.error('Error fetching report access users:', e);
    } finally {
      setLoadingReportAccess(false);
    }
  }, [getAuthToken, isAdmin]);

  // Toggle report access for a QC/QA user
  const toggleReportAccess = async (userId) => {
    try {
      const token = getAuthToken();
      const res = await axios.put(`${API}/admin/users/${userId}/report-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchReportAccessUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update report access');
    }
  };

  // Fetch Templates for Selected Reason
  const fetchTemplatesForReason = useCallback(async (reasonId) => {
    if (!reasonId) {
      setReasonTemplates([]);
      return;
    }
    setLoadingReasonTemplates(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/reports/reasons/${reasonId}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReasonTemplates(res.data || []);
    } catch (e) {
      console.error('Error fetching templates for reason:', e);
      setReasonTemplates([]);
    } finally {
      setLoadingReasonTemplates(false);
    }
  }, [getAuthToken]);

  // When reason changes, fetch its templates
  useEffect(() => {
    if (selectedReasonId) {
      fetchTemplatesForReason(selectedReasonId);
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setFormResponses({});
    }
  }, [selectedReasonId, fetchTemplatesForReason]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/reports/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data || []);
    } catch (e) {
      console.error('Error fetching templates:', e);
      toast.error('Failed to load report templates');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  const fetchJobs = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/jobs/public`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform the response to match expected format
      const jobsList = res.data?.jobs || res.data || [];
      setJobs(jobsList);
    } catch (e) {
      console.error('Error fetching jobs:', e);
      setJobs([]); // Set empty array on error
    }
  }, [getAuthToken]);

  const fetchSubmissions = useCallback(async () => {
    setSubmissionsLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.department) params.append('department', filters.department);
      if (filters.employee) params.append('employee', filters.employee);
      if (filters.templateId) params.append('template_id', filters.templateId);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const res = await axios.get(`${API}/reports/submissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(res.data.submissions || []);
    } catch (e) {
      console.error('Error fetching submissions:', e);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [getAuthToken, filters]);

  useEffect(() => {
    fetchTemplates();
    fetchJobs();
    fetchReasons();
    if (isAdmin) {
      fetchAllReasons();
      fetchReportAccessUsers();
    }
  }, [fetchTemplates, fetchJobs, fetchReasons, fetchAllReasons, fetchReportAccessUsers, isAdmin]);

  useEffect(() => {
    if (activeSubTab === 'outcomes') {
      fetchSubmissions();
    }
  }, [activeSubTab, fetchSubmissions]);

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId) {
      // First check in reasonTemplates (when reason flow is used)
      let template = reasonTemplates.find(t => t.id === selectedTemplateId);
      // Fallback to all templates
      if (!template) {
        template = templates.find(t => t.id === selectedTemplateId);
      }
      setSelectedTemplate(template);
      setFormResponses({});
    } else {
      setSelectedTemplate(null);
      setFormResponses({});
    }
  }, [selectedTemplateId, templates, reasonTemplates]);

  // Seed default templates
  const seedDefaultTemplates = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/reports/seed-default-templates`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed templates');
    }
  };

  // Seed default reasons
  const seedDefaultReasons = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/reports/reasons/seed-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchReasons();
      fetchAllReasons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed reasons');
    }
  };

  // Open reason dialog (create or edit)
  const openReasonDialog = (reason = null) => {
    if (reason) {
      setEditingReason(reason);
      setNewReasonName(reason.name);
      setNewReasonDescription(reason.description || '');
      setNewReasonTemplateIds(reason.template_ids || []);
      setNewReasonOrder(reason.order || 0);
      setNewReasonActive(reason.is_active !== false);
    } else {
      setEditingReason(null);
      setNewReasonName('');
      setNewReasonDescription('');
      setNewReasonTemplateIds([]);
      setNewReasonOrder(allReasons.length);
      setNewReasonActive(true);
    }
    setIsReasonDialogOpen(true);
  };

  // Save reason (create or update)
  const handleSaveReason = async () => {
    if (!newReasonName.trim()) {
      toast.error('Please enter a reason name');
      return;
    }

    setSavingReason(true);
    try {
      const token = getAuthToken();
      const reasonData = {
        name: newReasonName.trim(),
        description: newReasonDescription.trim(),
        template_ids: newReasonTemplateIds,
        order: newReasonOrder,
        is_active: newReasonActive
      };

      if (editingReason) {
        await axios.put(`${API}/reports/reasons/${editingReason.id}`, reasonData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reason updated successfully');
      } else {
        await axios.post(`${API}/reports/reasons`, reasonData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reason created successfully');
      }

      setIsReasonDialogOpen(false);
      fetchReasons();
      fetchAllReasons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save reason');
    } finally {
      setSavingReason(false);
    }
  };

  // Delete reason
  const handleDeleteReason = async (reasonId) => {
    if (!window.confirm('Are you sure you want to delete this reason?')) return;

    try {
      const token = getAuthToken();
      await axios.delete(`${API}/reports/reasons/${reasonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reason deleted');
      fetchReasons();
      fetchAllReasons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete reason');
    }
  };

  // Toggle template assignment for a reason
  const toggleTemplateForReason = (templateId) => {
    setNewReasonTemplateIds(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Check if a question should be visible based on conditional logic
  const isQuestionVisible = (question) => {
    if (!question.condition) return true;
    const dependentValue = formResponses[question.condition.depends_on_question_id];
    return dependentValue === question.condition.show_when_value;
  };

  // Handle form response change
  const handleResponseChange = (questionId, value) => {
    setFormResponses(prev => ({ ...prev, [questionId]: value }));
  };

  // Submit report
  const handleSubmitReport = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a report');
      return;
    }
    
    // Validate required fields
    const visibleRequiredQuestions = selectedTemplate.questions.filter(q => q.is_required && isQuestionVisible(q));
    const missingFields = visibleRequiredQuestions.filter(q => !formResponses[q.id] || formResponses[q.id] === '');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.map(q => q.question_text).join(', ')}`);
      return;
    }
    
    setSubmitting(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/reports/submit`, {
        template_id: selectedTemplateId,
        responses: formResponses
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report submitted successfully!');
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setFormResponses({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit submission dialog (Admin Only)
  const openEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setEditSubmissionResponses(submission.responses || {});
    setEditSubmissionStatus(submission.status || 'Pending Review');
    setIsEditSubmissionDialogOpen(true);
  };

  // Save edited submission (Admin Only)
  const handleSaveSubmission = async () => {
    if (!editingSubmission) return;
    
    setSavingSubmission(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/reports/submissions/${editingSubmission.id}`, {
        responses: editSubmissionResponses,
        status: editSubmissionStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission updated successfully!');
      setIsEditSubmissionDialogOpen(false);
      setEditingSubmission(null);
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update submission');
    } finally {
      setSavingSubmission(false);
    }
  };

  // Open QC Review dialog
  const openReviewDialog = (submission) => {
    setReviewingSubmission(submission);
    setReviewDefects(submission.defects || []);
    setReviewFeedback(submission.qc_feedback || '');
    setReviewStatus(submission.qc_status || 'pending');
    setIsReviewDialogOpen(true);
  };

  // Save QC Review
  const handleSaveReview = async () => {
    if (!reviewingSubmission) return;
    
    setSavingReview(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/reports/submissions/${reviewingSubmission.id}/qc-review`, {
        defects: reviewDefects,
        qc_feedback: reviewFeedback,
        qc_status: reviewStatus,
        reviewed_by: currentUser?.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review saved successfully!');
      setIsReviewDialogOpen(false);
      setReviewingSubmission(null);
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save review');
    } finally {
      setSavingReview(false);
    }
  };

  // Add defect to review
  const addDefect = () => {
    setReviewDefects([...reviewDefects, { field: '', description: '', severity: 'minor' }]);
  };

  // Update defect
  const updateDefect = (index, field, value) => {
    const updated = [...reviewDefects];
    updated[index] = { ...updated[index], [field]: value };
    setReviewDefects(updated);
  };

  // Remove defect
  const removeDefect = (index) => {
    setReviewDefects(reviewDefects.filter((_, i) => i !== index));
  };

  // Open QA Feedback dialog (QA giving feedback to QC)
  const openQAFeedbackDialog = (submission) => {
    setQaFeedbackSubmission(submission);
    setQaFeedbackText(submission.qa_feedback || '');
    setQaFeedbackDialogOpen(true);
  };

  // Save QA Feedback to QC
  const handleSaveQAFeedback = async () => {
    if (!qaFeedbackSubmission) return;
    
    setSavingQaFeedback(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/reports/submissions/${qaFeedbackSubmission.id}/qa-feedback`, {
        qa_feedback: qaFeedbackText,
        qa_reviewed_by: currentUser?.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('QA feedback saved!');
      setQaFeedbackDialogOpen(false);
      setQaFeedbackSubmission(null);
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save QA feedback');
    } finally {
      setSavingQaFeedback(false);
    }
  };

  // Open Comment/Update Dialog
  const openCommentDialog = (submission, isUpdate = false) => {
    setCommentSubmission(submission);
    setCommentText('');
    setIsUpdateComment(isUpdate);
    setCommentDialogOpen(true);
  };

  // Save Comment/Update
  const handleSaveComment = async () => {
    if (!commentSubmission || !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    setSavingComment(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/reports/submissions/${commentSubmission.id}/comment`, {
        message: commentText,
        is_update: isUpdateComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isUpdateComment ? 'Update posted! QA/QC/Admin notified.' : 'Comment added!');
      setCommentDialogOpen(false);
      setCommentSubmission(null);
      setCommentText('');
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save comment');
    } finally {
      setSavingComment(false);
    }
  };

  // Toggle submission selection
  const toggleSubmissionSelection = (submissionId) => {
    setSelectedSubmissions(prev => {
      if (prev.includes(submissionId)) {
        return prev.filter(id => id !== submissionId);
      }
      return [...prev, submissionId];
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedSubmissions([]);
      setSelectAllChecked(false);
    } else {
      const filteredSubmissions = getFilteredSubmissions();
      setSelectedSubmissions(filteredSubmissions.map(s => s.id));
      setSelectAllChecked(true);
    }
  };

  // Get filtered submissions
  const getFilteredSubmissions = () => {
    return submissions.filter(sub => {
      if (filters.templateId && filters.templateId !== 'all' && sub.template_id !== filters.templateId) return false;
      if (filters.status && filters.status !== 'all' && sub.status !== filters.status) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!sub.submitted_by_name?.toLowerCase().includes(searchLower) &&
            !sub.template_name?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      return true;
    });
  };

  // Bulk delete submissions
  const handleBulkDelete = async () => {
    if (selectedSubmissions.length === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedSubmissions.length} submission(s)? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingSubmissions(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/reports/submissions/bulk-delete`, {
        submission_ids: selectedSubmissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedSubmissions.length} submission(s) deleted`);
      setSelectedSubmissions([]);
      setSelectAllChecked(false);
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete submissions');
    } finally {
      setDeletingSubmissions(false);
    }
  };

  // Render form field based on question type
  const renderFormField = (question) => {
    const value = formResponses[question.id] || '';
    
    switch (question.question_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={question.question_type === 'email' ? 'email' : question.question_type === 'phone' ? 'tel' : 'text'}
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder={question.placeholder || `Enter ${question.question_text.toLowerCase()}`}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder={question.placeholder || `Enter ${question.question_text.toLowerCase()}`}
            rows={3}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="0"
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
          />
        );
      case 'dropdown':
        return (
          <Select value={value} onValueChange={(v) => handleResponseChange(question.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {(question.options || []).map((opt, i) => (
                <SelectItem key={i} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'yes_no':
        return (
          <Select value={value} onValueChange={(v) => handleResponseChange(question.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select Yes or No" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {(question.options || []).map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={question.id}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  className="w-4 h-4 text-violet-600"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        const checkedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(question.options || []).map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checkedValues.includes(opt)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkedValues, opt]
                      : checkedValues.filter(v => v !== opt);
                    handleResponseChange(question.id, newValues);
                  }}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'file':
        const isUploading = uploadingFiles[question.id];
        const currentValue = value;
        const hasUploadedFile = currentValue && (currentValue.includes('/api/') || currentValue.startsWith('http'));
        
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(question.id, file);
                  }
                }}
                className="flex-1"
              />
              {isUploading && (
                <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
              )}
            </div>
            {hasUploadedFile && isImageResponse(currentValue) && (
              <div className="mt-2 p-2 bg-slate-50 rounded-lg border">
                <img 
                  src={getImageUrl(currentValue)} 
                  alt="Uploaded preview" 
                  className="max-h-32 rounded object-contain"
                />
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> File uploaded
                </p>
              </div>
            )}
            {hasUploadedFile && !isImageResponse(currentValue) && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> File uploaded: {currentValue.split('/').pop()}
              </p>
            )}
          </div>
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
          />
        );
    }
  };

  // Template CRUD operations
  const handleCreateTemplate = async () => {
    if (!newTemplateName) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      const token = getAuthToken();
      await axios.post(`${API}/reports/templates`, {
        name: newTemplateName,
        description: newTemplateDescription,
        allowed_departments: newTemplateDepartments,
        job_ids: newTemplateJobIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template created');
      setIsTemplateDialogOpen(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      setNewTemplateDepartments([]);
      setNewTemplateJobIds([]);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/reports/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
      if (editingTemplate?.id === templateId) setEditingTemplate(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API}/reports/templates/${templateId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const handleArchiveTemplate = async (templateId) => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/reports/templates/${templateId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive template');
    }
  };

  // Question operations
  const handleAddQuestion = async () => {
    if (!editingTemplate) return;
    try {
      const token = getAuthToken();
      const newQuestion = {
        question_text: 'New Question',
        question_type: 'text',
        is_required: false,
        options: []
      };
      await axios.post(`${API}/reports/templates/${editingTemplate.id}/questions`, newQuestion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question added');
      // Refresh template
      const res = await axios.get(`${API}/reports/templates/${editingTemplate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingTemplate(res.data);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add question');
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingTemplate || !editingQuestion) return;
    try {
      const token = getAuthToken();
      await axios.put(
        `${API}/reports/templates/${editingTemplate.id}/questions/${editingQuestion.id}`,
        editingQuestion,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Question updated');
      setIsQuestionDialogOpen(false);
      // Refresh template
      const res = await axios.get(`${API}/reports/templates/${editingTemplate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingTemplate(res.data);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!editingTemplate) return;
    if (!window.confirm('Delete this question?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/reports/templates/${editingTemplate.id}/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted');
      // Refresh template
      const res = await axios.get(`${API}/reports/templates/${editingTemplate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingTemplate(res.data);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete question');
    }
  };

  // Format submission for display
  const formatSubmissionDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Group submissions by date for date-wise display
  const groupSubmissionsByDate = (subs) => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    subs.forEach(sub => {
      const subDate = new Date(sub.created_at);
      subDate.setHours(0, 0, 0, 0);
      const dateKey = subDate.toISOString().split('T')[0]; // YYYY-MM-DD
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          dateKey,
          displayDate: subDate.toLocaleDateString('en-IN', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric',
            weekday: 'long'
          }),
          isToday: subDate.getTime() === today.getTime(),
          submissions: []
        };
      }
      groups[dateKey].submissions.push(sub);
    });
    
    // Sort by date (newest first) and return as array
    return Object.values(groups).sort((a, b) => new Date(b.dateKey) - new Date(a.dateKey));
  };

  // Toggle date group expansion
  const toggleDateExpansion = (dateKey) => {
    setExpandedDates(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  // Get grouped submissions
  const groupedSubmissions = groupSubmissionsByDate(submissions);

  // Initialize today's date as expanded
  React.useEffect(() => {
    const grouped = groupSubmissionsByDate(submissions);
    if (grouped.length > 0) {
      setExpandedDates(prev => {
        const newState = { ...prev };
        // Always expand today's submissions if they exist
        grouped.forEach(group => {
          if (group.isToday && newState[group.dateKey] === undefined) {
            newState[group.dateKey] = true;
          }
          // Also expand the first (most recent) group if no today exists
          if (grouped[0]?.dateKey === group.dateKey && newState[group.dateKey] === undefined) {
            newState[group.dateKey] = true;
          }
        });
        return newState;
      });
    }
  }, [submissions]);

  return (
    <div className="space-y-6" data-testid="team-reports-content">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-50 via-purple-50/30 to-indigo-50/40 rounded-2xl p-6 border border-violet-100/80 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <ClipboardCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                Team Activity Reports
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">Submit and manage team activity reports</p>
            </div>
          </div>
          {isAdmin && templates.length === 0 && (
            <Button onClick={seedDefaultTemplates} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Default Templates
            </Button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-5' : 'grid-cols-2'} lg:w-auto lg:inline-grid`}>
          <TabsTrigger value="submit" className="gap-2">
            <Send className="h-4 w-4" />
            Submit Report
          </TabsTrigger>
          <TabsTrigger value="outcomes" className="gap-2">
            <Eye className="h-4 w-4" />
            Outcomes
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="reasons" className="gap-2">
                <Tag className="h-4 w-4" />
                Manage Reasons
              </TabsTrigger>
              <TabsTrigger value="access" className="gap-2">
                <Shield className="h-4 w-4" />
                Report Access
              </TabsTrigger>
              <TabsTrigger value="builder" className="gap-2">
                <Settings className="h-4 w-4" />
                Form Builder
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* TAB 1: Submit Report */}
        <TabsContent value="submit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-violet-600" />
                Submit Report
              </CardTitle>
              <CardDescription>Select a reason and report category to fill in the details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: Reason Selector */}
              <div className="space-y-2">
                <Label>Select Reason *</Label>
                <Select 
                  value={selectedReasonId} 
                  onValueChange={(v) => {
                    setSelectedReasonId(v);
                    setSelectedTemplateId('');
                    setSelectedTemplate(null);
                    setFormResponses({});
                  }}
                >
                  <SelectTrigger className="w-full md:w-96">
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map(reason => (
                      <SelectItem key={reason.id} value={reason.id}>{reason.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {reasons.length === 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-600">No reasons configured. {isAdmin ? 'Click below to add default reasons or go to Manage Reasons tab.' : 'Please contact admin to configure reasons.'}</p>
                    {isAdmin && (
                      <Button 
                        onClick={seedDefaultReasons} 
                        variant="outline" 
                        size="sm"
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Seed Default Reasons
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: Report Category/Template Selector - Only show when reason is selected */}
              {selectedReasonId && (
                <div className="space-y-2">
                  <Label>Select Report Category *</Label>
                  {loadingReasonTemplates ? (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading categories...
                    </div>
                  ) : (
                    <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                      <SelectTrigger className="w-full md:w-96">
                        <SelectValue placeholder="Choose a report category" />
                      </SelectTrigger>
                      <SelectContent>
                        {reasonTemplates.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {!loadingReasonTemplates && reasonTemplates.length === 0 && (
                    <p className="text-sm text-amber-600">No report categories assigned to this reason. {isAdmin ? 'Go to Manage Reasons tab to assign templates.' : 'Please contact admin.'}</p>
                  )}
                </div>
              )}

              {/* Dynamic Form */}
              {selectedTemplate && (
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl border">
                  <h3 className="font-semibold text-lg text-slate-800">{selectedTemplate.name}</h3>
                  {selectedTemplate.description && (
                    <p className="text-sm text-slate-500">{selectedTemplate.description}</p>
                  )}
                  
                  <div className="space-y-4 mt-4">
                    {(selectedTemplate.questions || [])
                      .sort((a, b) => a.order - b.order)
                      .map(question => {
                        if (!isQuestionVisible(question)) return null;
                        return (
                          <div key={question.id} className="space-y-1.5">
                            <Label className="flex items-center gap-1">
                              {question.question_text}
                              {question.is_required && <span className="text-red-500">*</span>}
                            </Label>
                            {renderFormField(question)}
                          </div>
                        );
                      })}
                  </div>

                  <Button 
                    onClick={handleSubmitReport} 
                    disabled={submitting}
                    className="w-full mt-4 bg-violet-600 hover:bg-violet-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Report
                      </>
                    )}
                  </Button>
                </div>
              )}

              {!selectedTemplate && reasons.length > 0 && (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                  <p>Select a report type to get started</p>
                </div>
              )}

              {templates.length === 0 && !loading && (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="h-12 w-12 mx-auto text-amber-300 mb-2" />
                  <p>No report templates available</p>
                  {isAdmin && (
                    <Button onClick={seedDefaultTemplates} variant="outline" className="mt-4">
                      Create Default Templates
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Outcomes */}
        <TabsContent value="outcomes" className="mt-6 space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">From Date</Label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                    className="w-36"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To Date</Label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                    className="w-36"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Report Type</Label>
                  <Select value={filters.templateId || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, templateId: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Reports" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Reports</SelectItem>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Pending Review">Pending Review</SelectItem>
                      <SelectItem value="Flagged">Flagged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <Label className="text-xs">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by name or report..."
                      value={filters.search}
                      onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                      className="pl-9"
                    />
                  </div>
                </div>
                <Button variant="outline" onClick={() => setFilters({ dateFrom: '', dateTo: '', department: '', employee: '', templateId: '', status: '', search: '' })}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
                <Button onClick={fetchSubmissions} disabled={submissionsLoading}>
                  {submissionsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4 mr-1" />}
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions Bar (Admin Only) */}
          {isAdmin && (
            <Card className="bg-slate-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectAllChecked}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Select All</span>
                    </label>
                    {selectedSubmissions.length > 0 && (
                      <span className="text-sm text-slate-500">
                        {selectedSubmissions.length} selected
                      </span>
                    )}
                  </div>
                  {selectedSubmissions.length > 0 && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleBulkDelete}
                      disabled={deletingSubmissions}
                    >
                      {deletingSubmissions ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-1" />
                      )}
                      Delete Selected ({selectedSubmissions.length})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submissions grouped by Date */}
          <div className="space-y-4">
            {submissionsLoading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-violet-500" />
                <p className="text-sm text-slate-500 mt-2">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">No submissions found</p>
              </div>
            ) : (
              groupedSubmissions.map(group => (
                <div key={group.dateKey} className="space-y-3">
                  {/* Date Header - Collapsible */}
                  <button
                    onClick={() => toggleDateExpansion(group.dateKey)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                      group.isToday 
                        ? 'bg-gradient-to-r from-violet-100 via-purple-100 to-indigo-100 border-2 border-violet-200 shadow-sm' 
                        : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        group.isToday ? 'bg-violet-500 text-white' : 'bg-slate-300 text-slate-600'
                      }`}>
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${group.isToday ? 'text-violet-800' : 'text-slate-700'}`}>
                            {group.displayDate}
                          </span>
                          {group.isToday && (
                            <Badge className="bg-violet-500 text-white text-xs">Today</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {group.submissions.length} report{group.submissions.length !== 1 ? 's' : ''} submitted
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${group.isToday ? 'text-violet-600' : 'text-slate-500'}`}>
                        {expandedDates[group.dateKey] ? 'Hide' : 'Show'}
                      </span>
                      {expandedDates[group.dateKey] ? (
                        <ChevronUp className={`h-5 w-5 ${group.isToday ? 'text-violet-600' : 'text-slate-500'}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${group.isToday ? 'text-violet-600' : 'text-slate-500'}`} />
                      )}
                    </div>
                  </button>

                  {/* Submissions for this date - Collapsible Content */}
                  {expandedDates[group.dateKey] && (
                    <div className="space-y-3 pl-4 border-l-2 border-slate-200 ml-5">
                      {group.submissions.map(sub => {
                        const { date, time } = formatSubmissionDate(sub.created_at);
                        return (
                          <Card key={sub.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-5">
                              <div className="flex items-start gap-4">
                                {/* Checkbox for Admin */}
                                {isAdmin && (
                                  <input
                                    type="checkbox"
                                    checked={selectedSubmissions.includes(sub.id)}
                                    onChange={() => toggleSubmissionSelection(sub.id)}
                                    className="mt-3 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                                
                                {/* Avatar */}
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                                  {sub.submitted_by_name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Header */}
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-slate-800">{sub.submitted_by_name}</span>
                                      <Badge variant="outline" className="text-xs">{sub.department || 'General'}</Badge>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                      <Clock className="h-3.5 w-3.5" />
                                      <span>{time}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Report Type + Status + Actions */}
                                  <div className="flex items-center justify-between mt-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge className="bg-violet-100 text-violet-700">{sub.template_name}</Badge>
                                      <Badge className={
                                        sub.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                        sub.status === 'Flagged' ? 'bg-red-100 text-red-700' :
                                        'bg-amber-100 text-amber-700'
                                      }>
                                        {sub.status}
                                      </Badge>
                                      {sub.qc_status && (
                                        <Badge className={
                                          sub.qc_status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                          sub.qc_status === 'defect' ? 'bg-red-100 text-red-700' :
                                          'bg-slate-100 text-slate-600'
                                        }>
                                          QC: {sub.qc_status}
                                        </Badge>
                                      )}
                                      {sub.defects?.length > 0 && (
                                        <Badge className="bg-red-100 text-red-700">
                                          {sub.defects.length} Defect(s)
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {/* QC Review Button */}
                                      {canReview && (
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openReviewDialog(sub); }}
                                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                          <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
                                          QC Review
                                        </Button>
                                      )}
                                      {/* QA Feedback Button (only if QC has reviewed) */}
                                      {canGiveQAFeedback && sub.qc_status && (
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openQAFeedbackDialog(sub); }}
                                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                        >
                                          <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                          QA Feedback
                                        </Button>
                                      )}
                                      {/* Admin Edit Button */}
                                      {isAdmin && (
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openEditSubmission(sub); }}
                                          className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                        >
                                          <Pencil className="h-3.5 w-3.5 mr-1" />
                                          Edit
                                        </Button>
                                      )}
                                      {/* Comment/Update Button - visible to owner and QA/QC/Admin */}
                                      {(sub.submitted_by === currentUser?.email || isAdmin || canReview || canGiveQAFeedback) && (
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openCommentDialog(sub, sub.submitted_by === currentUser?.email); }}
                                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                        >
                                          <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                          {sub.submitted_by === currentUser?.email ? 'Update' : 'Comment'}
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Comments/Updates Display */}
                                  {sub.comments?.length > 0 && (
                                    <div className="mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                                      <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3" />
                                        Comments & Updates ({sub.comments.length})
                                      </p>
                                      <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {sub.comments.map((comment, idx) => (
                                          <div key={comment.id || idx} className={`text-sm p-2 rounded ${comment.is_update ? 'bg-amber-100 border-l-2 border-amber-500' : 'bg-white'}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="font-medium text-slate-700">{comment.user_name}</span>
                                              <Badge variant="outline" className="text-[10px] py-0">{comment.user_role}</Badge>
                                              {comment.is_update && <Badge className="bg-amber-500 text-white text-[10px] py-0">Update</Badge>}
                                            </div>
                                            <p className="text-slate-600">{comment.message}</p>
                                            <p className="text-[10px] text-slate-400 mt-1">{new Date(comment.created_at).toLocaleString()}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* QC Feedback Display */}
                                  {sub.qc_feedback && (
                                    <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                                      <p className="text-xs font-semibold text-blue-700 mb-1">QC Feedback:</p>
                                      <p className="text-sm text-blue-800">{sub.qc_feedback}</p>
                                      {sub.qc_reviewed_by && (
                                        <p className="text-xs text-blue-500 mt-1">Reviewed by: {sub.qc_reviewed_by}</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Defects Display */}
                                  {sub.defects?.length > 0 && (
                                    <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-100">
                                      <p className="text-xs font-semibold text-red-700 mb-1">Defects Found:</p>
                                      {sub.defects.map((defect, idx) => (
                                        <div key={idx} className="text-sm text-red-800 flex items-start gap-2 mb-1">
                                          <span className="font-medium">•</span>
                                          <span>
                                            <span className={`text-xs px-1 py-0.5 rounded ${defect.severity === 'major' ? 'bg-red-200' : 'bg-amber-200'}`}>
                                              {defect.severity}
                                            </span>
                                            {' '}{defect.description}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* QA Feedback to QC Display */}
                                  {sub.qa_feedback && (
                                    <div className="mt-2 p-2 bg-purple-50 rounded-lg border border-purple-100">
                                      <p className="text-xs font-semibold text-purple-700 mb-1">QA Feedback to QC:</p>
                                      <p className="text-sm text-purple-800">{sub.qa_feedback}</p>
                                      {sub.qa_reviewed_by && (
                                        <p className="text-xs text-purple-500 mt-1">From: {sub.qa_reviewed_by}</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Responses */}
                                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                                      {sub.questions_snapshot?.map(q => {
                                        const response = sub.responses[q.id];
                                        if (response === undefined || response === '' || response === null) return null;
                                        
                                        // Check if response is an image and has a valid URL
                                        const responseIsImage = isImageResponse(response);
                                        const responseHasValidUrl = hasValidImageUrl(response);
                                        
                                        return (
                                          <div key={q.id} className={`flex flex-col gap-1 ${responseIsImage && responseHasValidUrl ? 'col-span-1 md:col-span-2 lg:col-span-3' : ''}`}>
                                            <span className="text-slate-500 flex-shrink-0">{q.question_text}:</span>
                                            {responseIsImage && responseHasValidUrl ? (
                                              <div className="mt-1">
                                                <a 
                                                  href={getImageUrl(response)} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="inline-block"
                                                >
                                                  <img 
                                                    src={getImageUrl(response)} 
                                                    alt={q.question_text}
                                                    className="max-h-48 max-w-full rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                                    onError={(e) => {
                                                      e.target.style.display = 'none';
                                                      e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                  />
                                                  <div className="hidden items-center gap-2 text-slate-500 p-2 bg-white rounded border">
                                                    <ImageIcon className="h-4 w-4" />
                                                    <span className="text-xs">{response.split('/').pop() || 'Image file'}</span>
                                                    <ExternalLink className="h-3 w-3" />
                                                  </div>
                                                </a>
                                              </div>
                                            ) : responseIsImage && !responseHasValidUrl ? (
                                              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                                <ImageIcon className="h-4 w-4 flex-shrink-0" />
                                                <span className="font-medium break-words">{response}</span>
                                                <span className="text-xs text-amber-500">(file reference)</span>
                                              </div>
                                            ) : (
                                              <span className="font-medium text-slate-800 break-words">
                                                {Array.isArray(response) ? response.join(', ') : String(response)}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* TAB 3: Manage Reasons (Admin Only) */}
        {isAdmin && (
          <TabsContent value="reasons" className="mt-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Report Reasons</h3>
                <p className="text-sm text-slate-500">Manage reasons and assign templates to each reason</p>
              </div>
              <div className="flex gap-2">
                {allReasons.length === 0 && templates.length > 0 && (
                  <Button variant="outline" onClick={seedDefaultReasons}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Seed Default Reasons
                  </Button>
                )}
                <Button onClick={() => openReasonDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reason
                </Button>
              </div>
            </div>

            {/* Reasons List */}
            {allReasons.length === 0 ? (
              <Card className="p-8 text-center">
                <Tag className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h4 className="text-lg font-medium text-slate-600">No Reasons Yet</h4>
                <p className="text-sm text-slate-500 mt-2">Create reasons to organize your report categories</p>
                <Button className="mt-4" onClick={() => openReasonDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Reason
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allReasons.map((reason, index) => (
                  <Card 
                    key={reason.id} 
                    className={`relative transition-all hover:shadow-md ${!reason.is_active ? 'opacity-60 bg-slate-50' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">#{index + 1}</span>
                            {!reason.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                          </div>
                          <h4 className="font-semibold text-slate-800 mt-1">{reason.name}</h4>
                          {reason.description && (
                            <p className="text-xs text-slate-500 mt-1">{reason.description}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {reason.template_ids?.length || 0} template(s) assigned
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => openReasonDialog(reason)}
                            className="h-8 w-8"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleDeleteReason(reason.id)}
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Assigned Templates Preview */}
                      {reason.template_ids?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-medium text-slate-600 mb-2">Assigned Templates:</p>
                          <div className="flex flex-wrap gap-1">
                            {reason.template_ids.map(tid => {
                              const t = templates.find(tpl => tpl.id === tid);
                              return t ? (
                                <Badge key={tid} variant="outline" className="text-xs">{t.name}</Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Reason Dialog */}
            <Dialog open={isReasonDialogOpen} onOpenChange={setIsReasonDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingReason ? 'Edit Reason' : 'Create New Reason'}</DialogTitle>
                  <DialogDescription>
                    Define a reason and assign templates that should appear when this reason is selected.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Reason Name *</Label>
                    <Input 
                      value={newReasonName}
                      onChange={(e) => setNewReasonName(e.target.value)}
                      placeholder="e.g., Registration, Telephonic Round"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={newReasonDescription}
                      onChange={(e) => setNewReasonDescription(e.target.value)}
                      placeholder="Brief description of this reason"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Order</Label>
                    <Input 
                      type="number"
                      value={newReasonOrder}
                      onChange={(e) => setNewReasonOrder(parseInt(e.target.value) || 0)}
                      min={0}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={newReasonActive}
                      onCheckedChange={setNewReasonActive}
                    />
                    <Label>Active</Label>
                  </div>
                  
                  {/* Template Assignment */}
                  <div className="space-y-2">
                    <Label>Assign Templates</Label>
                    <p className="text-xs text-slate-500">Select which report forms should appear when this reason is selected</p>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {templates.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-2">No templates available. Create templates first in Form Builder.</p>
                      ) : (
                        templates.map(t => (
                          <div 
                            key={t.id}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${newReasonTemplateIds.includes(t.id) ? 'bg-violet-50 border border-violet-200' : ''}`}
                            onClick={() => toggleTemplateForReason(t.id)}
                          >
                            <input 
                              type="checkbox"
                              checked={newReasonTemplateIds.includes(t.id)}
                              onChange={() => {}}
                              className="rounded"
                            />
                            <span className="text-sm">{t.name}</span>
                            <span className="text-xs text-slate-400">({t.questions?.length || 0} questions)</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsReasonDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSaveReason} disabled={savingReason}>
                    {savingReason ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingReason ? 'Update Reason' : 'Create Reason'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* TAB 4: Report Access (Admin Only) */}
        {isAdmin && (
          <TabsContent value="access" className="mt-6 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  Report Access Management
                </CardTitle>
                <CardDescription>
                  Grant team members access to view all report outcomes. Users with access can see submissions from all team members, not just their own.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingReportAccess ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-slate-500">Loading users...</span>
                  </div>
                ) : reportAccessUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-slate-300" />
                    <p className="text-slate-500 mt-2">No team members found</p>
                    <p className="text-sm text-slate-400">Add team members to manage their report access</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reportAccessUsers.map(user => (
                      <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold">
                            {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{user.name || user.email}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <span className="capitalize px-1.5 py-0.5 bg-slate-100 rounded text-xs">{user.role || 'User'}</span>
                              {user.department && <span>• {user.department}</span>}
                              <span>• {user.email}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm px-2 py-1 rounded-full ${user.report_access_granted ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {user.report_access_granted ? '✓ Full Access' : 'Own Reports Only'}
                          </span>
                          <Button 
                            variant={user.report_access_granted ? "outline" : "default"}
                            size="sm"
                            onClick={() => toggleReportAccess(user.id)}
                          >
                            {user.report_access_granted ? 'Revoke Access' : 'Grant Access'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* TAB 5: Form Builder (Admin Only) */}
        {isAdmin && (
          <TabsContent value="builder" className="mt-6 space-y-4">
            {/* Template List + Actions */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Report Templates</h3>
              <Button onClick={() => { setNewTemplateName(''); setNewTemplateDescription(''); setIsTemplateDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <Card 
                  key={template.id} 
                  className={`cursor-pointer transition-all hover:shadow-lg hover:border-violet-300 ${editingTemplate?.id === template.id ? 'ring-2 ring-violet-500 bg-violet-50' : ''} ${template.is_archived ? 'opacity-60' : ''}`}
                  onClick={() => setEditingTemplate(template)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800">{template.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{template.questions?.length || 0} questions</p>
                        {template.is_archived && <Badge className="mt-1 bg-slate-200 text-slate-600">Archived</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDuplicateTemplate(template.id); }} title="Duplicate">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleArchiveTemplate(template.id); }} title="Archive">
                          <Archive className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }} className="text-red-500 hover:text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Edit Questions Button */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-violet-600 border-violet-200 hover:bg-violet-50 hover:border-violet-300"
                        onClick={(e) => { e.stopPropagation(); setEditingTemplate(template); }}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" />
                        Edit Questions
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Question Editor */}
            {editingTemplate && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{editingTemplate.name}</CardTitle>
                      <CardDescription>{editingTemplate.description || 'No description'}</CardDescription>
                    </div>
                    <Button onClick={handleAddQuestion}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(editingTemplate.questions || [])
                      .sort((a, b) => a.order - b.order)
                      .map((q, idx) => (
                        <div key={q.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors">
                          <GripVertical className="h-5 w-5 text-slate-400 cursor-grab" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">{idx + 1}. {q.question_text}</span>
                              {q.is_required && <Badge className="bg-red-100 text-red-600 text-[10px]">Required</Badge>}
                              {q.condition && <Badge className="bg-amber-100 text-amber-600 text-[10px]">Conditional</Badge>}
                            </div>
                            <span className="text-xs text-slate-500">{QUESTION_TYPES.find(t => t.value === q.question_type)?.label || q.question_type}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => { setEditingQuestion({...q}); setIsQuestionDialogOpen(true); }}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteQuestion(q.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    {(!editingTemplate.questions || editingTemplate.questions.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <p>No questions yet. Click &quot;Add Question&quot; to start.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>Create a new report template for specific jobs or all jobs</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Daily Registration Report"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Brief description of this report"
              />
            </div>
            <div className="space-y-2">
              <Label>Assign to Jobs (Optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Leave empty to make this template available for all jobs</p>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {jobs.length === 0 ? (
                  <p className="text-sm text-slate-400 p-2">No jobs available</p>
                ) : (
                  jobs.map(job => (
                    <label key={job.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTemplateJobIds.includes(job.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewTemplateJobIds(prev => [...prev, job.id]);
                          } else {
                            setNewTemplateJobIds(prev => prev.filter(id => id !== job.id));
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{job.title} - {job.company_name}</span>
                    </label>
                  ))
                )}
              </div>
              {newTemplateJobIds.length > 0 && (
                <p className="text-xs text-violet-600 mt-1">
                  Selected: {newTemplateJobIds.length} job(s)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsTemplateDialogOpen(false); setNewTemplateJobIds([]); }}>Cancel</Button>
            <Button onClick={handleCreateTemplate}>Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={isQuestionDialogOpen} onOpenChange={setIsQuestionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
          </DialogHeader>
          {editingQuestion && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Question Text *</Label>
                <Input
                  value={editingQuestion.question_text}
                  onChange={(e) => setEditingQuestion(q => ({ ...q, question_text: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Question Type</Label>
                <Select 
                  value={editingQuestion.question_type} 
                  onValueChange={(v) => setEditingQuestion(q => ({ ...q, question_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {['dropdown', 'radio', 'checkbox'].includes(editingQuestion.question_type) && (
                <div className="space-y-3">
                  <Label>Options</Label>
                  <div className="space-y-2">
                    {(editingQuestion.options || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-6">{idx + 1}.</span>
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const newOptions = [...(editingQuestion.options || [])];
                            newOptions[idx] = e.target.value;
                            setEditingQuestion(q => ({ ...q, options: newOptions }));
                          }}
                          placeholder={`Option ${idx + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOptions = (editingQuestion.options || []).filter((_, i) => i !== idx);
                            setEditingQuestion(q => ({ ...q, options: newOptions }));
                          }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newOptions = [...(editingQuestion.options || []), ''];
                      setEditingQuestion(q => ({ ...q, options: newOptions }));
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Option
                  </Button>
                  {(!editingQuestion.options || editingQuestion.options.length === 0) && (
                    <p className="text-xs text-slate-500 text-center">Click "Add Option" to create dropdown choices</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingQuestion.is_required}
                  onCheckedChange={(v) => setEditingQuestion(q => ({ ...q, is_required: v }))}
                />
                <Label>Required</Label>
              </div>
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium">Conditional Logic (Optional)</Label>
                <p className="text-xs text-slate-500 mb-2">Show this question only when another question has a specific answer</p>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Depends on Question</Label>
                    <Select 
                      value={editingQuestion.condition?.depends_on_question_id || 'none'} 
                      onValueChange={(v) => setEditingQuestion(q => ({ 
                        ...q, 
                        condition: v !== 'none' ? { depends_on_question_id: v, show_when_value: '' } : null 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="None (always show)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (always show)</SelectItem>
                        {editingTemplate?.questions?.filter(q => q.id !== editingQuestion.id).map(q => (
                          <SelectItem key={q.id} value={q.id}>
                            {q.question_text} 
                            {q.question_type === 'yes_no' && ' (Yes/No)'}
                            {q.question_type === 'dropdown' && ' (Dropdown)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {editingQuestion.condition?.depends_on_question_id && (() => {
                    const dependsOnQ = editingTemplate?.questions?.find(q => q.id === editingQuestion.condition.depends_on_question_id);
                    
                    // For Yes/No questions, show Yes/No radio buttons
                    if (dependsOnQ?.question_type === 'yes_no') {
                      return (
                        <div className="space-y-2 p-2 bg-white rounded border">
                          <Label className="text-xs font-medium">Show this question when answer is:</Label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="show_when"
                                checked={editingQuestion.condition?.show_when_value === 'Yes'}
                                onChange={() => setEditingQuestion(q => ({ 
                                  ...q, 
                                  condition: { ...q.condition, show_when_value: 'Yes' } 
                                }))}
                                className="text-violet-600"
                              />
                              <span className="text-sm">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="show_when"
                                checked={editingQuestion.condition?.show_when_value === 'No'}
                                onChange={() => setEditingQuestion(q => ({ 
                                  ...q, 
                                  condition: { ...q.condition, show_when_value: 'No' } 
                                }))}
                                className="text-violet-600"
                              />
                              <span className="text-sm">No</span>
                            </label>
                          </div>
                        </div>
                      );
                    }
                    
                    // For dropdown/radio questions, show the options
                    if (dependsOnQ?.question_type === 'dropdown' || dependsOnQ?.question_type === 'radio') {
                      return (
                        <div className="space-y-2 p-2 bg-white rounded border">
                          <Label className="text-xs font-medium">Show when selected option is:</Label>
                          <Select
                            value={editingQuestion.condition?.show_when_value || ''}
                            onValueChange={(v) => setEditingQuestion(q => ({ 
                              ...q, 
                              condition: { ...q.condition, show_when_value: v } 
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {(dependsOnQ.options || []).map((opt, i) => (
                                <SelectItem key={i} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }
                    
                    // Default: text input for value
                    return (
                      <div className="space-y-1 p-2 bg-white rounded border">
                        <Label className="text-xs font-medium">Show when value equals:</Label>
                        <Input
                          value={editingQuestion.condition?.show_when_value || ''}
                          onChange={(e) => setEditingQuestion(q => ({ 
                            ...q, 
                            condition: { ...q.condition, show_when_value: e.target.value } 
                          }))}
                          placeholder="Enter the value that triggers this question"
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsQuestionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateQuestion}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Submission Dialog (Admin Only) */}
      <Dialog open={isEditSubmissionDialogOpen} onOpenChange={setIsEditSubmissionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-violet-600" />
              Edit Submission
            </DialogTitle>
            <DialogDescription>
              {editingSubmission && (
                <span>Editing submission by <strong>{editingSubmission.submitted_by_name}</strong> - {editingSubmission.template_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {editingSubmission && (
            <div className="space-y-4 py-4">
              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editSubmissionStatus} onValueChange={setEditSubmissionStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending Review">Pending Review</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Flagged">Flagged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Responses */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Responses</Label>
                {editingSubmission.questions_snapshot?.map(q => (
                  <div key={q.id} className="space-y-1.5 p-3 bg-slate-50 rounded-lg">
                    <Label className="text-sm">{q.question_text}</Label>
                    {q.question_type === 'textarea' ? (
                      <Textarea
                        value={editSubmissionResponses[q.id] || ''}
                        onChange={(e) => setEditSubmissionResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                        rows={3}
                      />
                    ) : q.question_type === 'yes_no' ? (
                      <Select 
                        value={editSubmissionResponses[q.id] || ''} 
                        onValueChange={(v) => setEditSubmissionResponses(prev => ({ ...prev, [q.id]: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : q.question_type === 'dropdown' || q.question_type === 'radio' ? (
                      <Select 
                        value={editSubmissionResponses[q.id] || ''} 
                        onValueChange={(v) => setEditSubmissionResponses(prev => ({ ...prev, [q.id]: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {q.options?.map((opt, i) => (
                            <SelectItem key={i} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={q.question_type === 'number' ? 'number' : q.question_type === 'date' ? 'date' : 'text'}
                        value={editSubmissionResponses[q.id] || ''}
                        onChange={(e) => setEditSubmissionResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSubmissionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSubmission} disabled={savingSubmission}>
              {savingSubmission ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC Review Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-blue-600" />
              QC Review
            </DialogTitle>
            <DialogDescription>
              {reviewingSubmission && (
                <span>Review submission by <strong>{reviewingSubmission.submitted_by_name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {reviewingSubmission && (
            <div className="space-y-4 py-4">
              {/* Review Status */}
              <div className="space-y-2">
                <Label className="font-semibold">Review Status *</Label>
                <Select value={reviewStatus} onValueChange={setReviewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending Review</SelectItem>
                    <SelectItem value="approved">Approved - No Defects</SelectItem>
                    <SelectItem value="defect">Defect Found</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Defects Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Defects</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addDefect}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Defect
                  </Button>
                </div>
                
                {reviewDefects.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
                    No defects marked. Click "Add Defect" if you find any issues.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {reviewDefects.map((defect, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-red-700">Defect #{idx + 1}</span>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeDefect(idx)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-100 h-7 w-7 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Field/Question</Label>
                            <Select 
                              value={defect.field || ''} 
                              onValueChange={(v) => updateDefect(idx, 'field', v)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select field" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">General / Overall</SelectItem>
                                {reviewingSubmission.questions_snapshot?.map(q => (
                                  <SelectItem key={q.id} value={q.id}>{q.question_text}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Severity</Label>
                            <Select 
                              value={defect.severity || 'minor'} 
                              onValueChange={(v) => updateDefect(idx, 'severity', v)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="minor">Minor</SelectItem>
                                <SelectItem value="major">Major</SelectItem>
                                <SelectItem value="critical">Critical</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Description</Label>
                          <Textarea
                            value={defect.description || ''}
                            onChange={(e) => updateDefect(idx, 'description', e.target.value)}
                            placeholder="Describe the defect..."
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Overall Feedback */}
              <div className="space-y-2">
                <Label className="font-semibold">QC Feedback</Label>
                <Textarea
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                  placeholder="Add overall feedback or comments for this submission..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReviewDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveReview} 
              disabled={savingReview || !reviewStatus}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingReview ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Review
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QA Feedback to QC Dialog */}
      <Dialog open={qaFeedbackDialogOpen} onOpenChange={setQaFeedbackDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              QA Feedback to QC
            </DialogTitle>
            <DialogDescription>
              {qaFeedbackSubmission && (
                <span>
                  Provide feedback on QC review by <strong>{qaFeedbackSubmission.qc_reviewed_by || 'QC Team'}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {qaFeedbackSubmission && (
            <div className="space-y-4 py-4">
              {/* Show QC Review Summary */}
              {qaFeedbackSubmission.qc_status && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-1">QC Review Summary:</p>
                  <p className="text-sm text-blue-800">
                    Status: <Badge className={qaFeedbackSubmission.qc_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {qaFeedbackSubmission.qc_status}
                    </Badge>
                  </p>
                  {qaFeedbackSubmission.defects?.length > 0 && (
                    <p className="text-sm text-blue-800 mt-1">Defects found: {qaFeedbackSubmission.defects.length}</p>
                  )}
                  {qaFeedbackSubmission.qc_feedback && (
                    <p className="text-sm text-blue-800 mt-1 italic">"{qaFeedbackSubmission.qc_feedback}"</p>
                  )}
                </div>
              )}
              
              {/* QA Feedback */}
              <div className="space-y-2">
                <Label className="font-semibold">Your Feedback to QC</Label>
                <Textarea
                  value={qaFeedbackText}
                  onChange={(e) => setQaFeedbackText(e.target.value)}
                  placeholder="Provide feedback on the QC review... (e.g., review quality, missed issues, good catches)"
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQaFeedbackDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveQAFeedback} 
              disabled={savingQaFeedback || !qaFeedbackText.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingQaFeedback ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Send Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment/Update Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
              {isUpdateComment ? 'Post Update / Correction' : 'Add Comment'}
            </DialogTitle>
          </DialogHeader>
          
          {commentSubmission && (
            <div className="space-y-4">
              {/* Submission Info */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Report:</span> {commentSubmission.template_name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Submitted by:</span> {commentSubmission.submitted_by_name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Date:</span> {new Date(commentSubmission.created_at).toLocaleString()}
                </p>
              </div>
              
              {/* Update Checkbox - only for owners */}
              {commentSubmission.submitted_by === currentUser?.email && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input
                    type="checkbox"
                    id="isUpdate"
                    checked={isUpdateComment}
                    onChange={(e) => setIsUpdateComment(e.target.checked)}
                    className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <Label htmlFor="isUpdate" className="text-amber-700 cursor-pointer">
                    This is a correction/update (QA, QC, and Admin will be notified)
                  </Label>
                </div>
              )}
              
              {/* Comment Text */}
              <div className="space-y-2">
                <Label className="font-semibold">
                  {isUpdateComment ? 'Describe your update/correction' : 'Your comment'}
                </Label>
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={isUpdateComment 
                    ? "Explain what you're updating or correcting (e.g., 'Corrected the candidate name from X to Y')" 
                    : "Add your comment or note..."
                  }
                  rows={4}
                />
              </div>
              
              {/* Existing Comments */}
              {commentSubmission.comments?.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Previous Comments ({commentSubmission.comments.length})</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {commentSubmission.comments.slice(-3).map((c, idx) => (
                      <div key={idx} className={`text-xs p-2 rounded ${c.is_update ? 'bg-amber-50' : 'bg-slate-50'}`}>
                        <span className="font-medium">{c.user_name}</span>: {c.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveComment} 
              disabled={savingComment || !commentText.trim()}
              className={isUpdateComment ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {savingComment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {isUpdateComment ? <AlertCircle className="h-4 w-4 mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                  {isUpdateComment ? 'Post Update' : 'Add Comment'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamActivityReports;
